import React from 'react';
import SeasonsActions from './SeasonsActions';
import { generateComponentDocs, generateStoryDocs } from '../../utils/documentationUtils';

// Storybook metadata
export default {
  title: 'Components/Seasons/SeasonsActions',
  component: SeasonsActions,
  parameters: {
    layout: 'centered',
    ...generateComponentDocs(
      // What problem does it solve?
      `\nThe \`SeasonsActions\` component provides action buttons for managing seasons, including editing, exporting, and deleting.
        \n\n###_**Key Features:**_
        \n- Displays relevant action buttons based on user roles.
        \n- Allows managers to edit and delete seasons.
        \n- Enables all users to export season data.\n\n`,
      // When & how to use it?
      `\n\nUse the \`SeasonsActions\` component to render action buttons for season management in a table row.
        \n\n###_**Key Features:**_
        \n- **Edit Button**:
        \n  - Available for managers.
        \n  - Triggers an edit function.
        \n\n- **Export Button**:
        \n  - Available for all users.
        \n  - Triggers an export function.
        \n\n- **Delete Button**:
        \n  - Available for managers.
        \n  - Triggers a delete function.\n\n`,
      // Current Usage
      '\nThe `SeasonsActions` component is used in the Seasons table to provide row-level actions for managing seasons. It adapts the available actions based on the user\'s role.\n\n',
      // Related Components
      '\n- **[Icon](http://localhost:6006/?path=/docs/components-icons-icon--docs)**: Displays the action icons for edit, export, and delete.\n\n',
    ),
  },
  tags: ['autodocs'],
  argTypes: {
    isManager: {
      control: { type: 'boolean' },
      description: 'Indicates if the user is a manager with additional permissions.',
      table: {
        type: { summary: 'boolean' },
        required: true,
      },
    },
    onDelete: {
      control: { type: 'function' },
      description: 'Callback function triggered when the delete button is clicked.',
      table: {
        type: { summary: 'function' },
        required: false,
      },
    },
    onExport: {
      control: { type: 'function' },
      description: 'Callback function triggered when the export button is clicked.',
      table: {
        type: { summary: 'function' },
        required: false,
      },
    },
    onEdit: {
      control: { type: 'function' },
      description: 'Callback function triggered when the edit button is clicked.',
      table: {
        type: { summary: 'function' },
        required: false,
      },
    },
  },
};

// Template for the stories
const Template = (args) => (
  <SeasonsActions {...args} />
);

// Story for manager with all actions
export const ManagerActions = Template.bind({});
ManagerActions.args = {
  isManager: true,
  onDelete: () => {},
  onExport: () => {},
  onEdit: () => {},
};
ManagerActions.parameters = generateStoryDocs(
  // When is this variant used?
  '\nUse this variant to display all action buttons for a manager, including edit, export, and delete.\n\n',
  // Where is it currently used on the product?
  '\nThe `SeasonsActions` component is used in the Seasons table for row-level actions.\n\n',
);

// Story for non-manager with limited actions
export const NonManagerActions = Template.bind({});
NonManagerActions.args = {
  isManager: false,
  onExport: () => {},
};
NonManagerActions.parameters = generateStoryDocs(
  // When is this variant used?
  '\nUse this variant to display limited actions for non-manager users, such as the export button.\n\n',
  // Where is it currently used on the product?
  '\nThe `SeasonsActions` component is used in the Seasons table for row-level actions.\n\n',
);
